import { useState, useEffect } from "react";
import { Header } from "@/components/Header";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import API from "@/lib/api";
import { Save, Check, Sun, Moon, Monitor } from "lucide-react";

export default function SettingsPage() {
  const { theme, setTheme: setThemeValue } = useTheme();
  const [settings, setSettings] = useState({
    default_prompt_length: "standard",
    preferred_style: "concise",
    default_platform: "general",
    output_structure: "categorized",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data } = await API.get("/settings");
      setSettings({
        default_prompt_length: data.default_prompt_length || "standard",
        preferred_style: data.preferred_style || "concise",
        default_platform: data.default_platform || "general",
        output_structure: data.output_structure || "categorized",
      });
    } catch (e) {
      console.error("Settings load error:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put("/settings", settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error("Settings save error:", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]" data-testid="settings-page">
      <Header />
      <main className="max-w-2xl mx-auto px-6 py-8">
        <h1 className="font-heading text-3xl font-bold tracking-tight mb-8 text-[var(--pf-text)]">Settings</h1>

        {loading ? (
          <div className="space-y-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-2">
                <div className="skeleton-line w-32 h-4" />
                <div className="skeleton-line w-full h-10" />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            <div className="border border-[var(--pf-border)] p-6">
              <h2 className="font-heading text-lg font-bold mb-5 text-[var(--pf-text)]">Appearance</h2>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-medium text-[var(--pf-text-secondary)] mb-3 block">
                  Theme
                </Label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: "light", label: "Light", icon: Sun },
                    { value: "dark", label: "Dark", icon: Moon },
                  ].map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      onClick={() => setThemeValue(value)}
                      data-testid={`theme-option-${value}`}
                      className={`flex items-center justify-center gap-2 px-4 py-3 border text-sm font-medium transition-colors duration-150 ${
                        theme === value
                          ? "border-[var(--pf-accent)] bg-[var(--pf-accent-subtle)] text-[var(--pf-accent)]"
                          : "border-[var(--pf-border)] text-[var(--pf-text-secondary)] hover:border-[var(--pf-accent)] hover:text-[var(--pf-text)]"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="border border-[var(--pf-border)] p-6">
              <h2 className="font-heading text-lg font-bold mb-5 text-[var(--pf-text)]">Generation Defaults</h2>

              <div className="space-y-5">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-medium text-[var(--pf-text-secondary)] mb-2 block">
                    Default Prompt Length
                  </Label>
                  <Select
                    value={settings.default_prompt_length}
                    onValueChange={(v) => setSettings({ ...settings, default_prompt_length: v })}
                  >
                    <SelectTrigger className="rounded-none border-[var(--pf-border)] h-10" data-testid="setting-prompt-length">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border-[var(--pf-border)]">
                      <SelectItem value="concise" className="rounded-none">Concise</SelectItem>
                      <SelectItem value="standard" className="rounded-none">Standard</SelectItem>
                      <SelectItem value="advanced" className="rounded-none">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-medium text-[var(--pf-text-secondary)] mb-2 block">
                    Preferred Style
                  </Label>
                  <Select
                    value={settings.preferred_style}
                    onValueChange={(v) => setSettings({ ...settings, preferred_style: v })}
                  >
                    <SelectTrigger className="rounded-none border-[var(--pf-border)] h-10" data-testid="setting-preferred-style">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border-[var(--pf-border)]">
                      <SelectItem value="concise" className="rounded-none">Concise</SelectItem>
                      <SelectItem value="balanced" className="rounded-none">Balanced</SelectItem>
                      <SelectItem value="detailed" className="rounded-none">Detailed</SelectItem>
                      <SelectItem value="technical" className="rounded-none">Technical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-medium text-[var(--pf-text-secondary)] mb-2 block">
                    Default Platform
                  </Label>
                  <Select
                    value={settings.default_platform}
                    onValueChange={(v) => setSettings({ ...settings, default_platform: v })}
                  >
                    <SelectTrigger className="rounded-none border-[var(--pf-border)] h-10" data-testid="setting-default-platform">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border-[var(--pf-border)]">
                      <SelectItem value="emergent" className="rounded-none">Emergent</SelectItem>
                      <SelectItem value="vibe-code" className="rounded-none">Vibe-Code Platforms</SelectItem>
                      <SelectItem value="ai-assistants" className="rounded-none">AI Coding Assistants</SelectItem>
                      <SelectItem value="no-code" className="rounded-none">No-Code Builders</SelectItem>
                      <SelectItem value="product-planning" className="rounded-none">Product Planning</SelectItem>
                      <SelectItem value="general" className="rounded-none">General</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-medium text-[var(--pf-text-secondary)] mb-2 block">
                    Output Structure
                  </Label>
                  <Select
                    value={settings.output_structure}
                    onValueChange={(v) => setSettings({ ...settings, output_structure: v })}
                  >
                    <SelectTrigger className="rounded-none border-[var(--pf-border)] h-10" data-testid="setting-output-structure">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border-[var(--pf-border)]">
                      <SelectItem value="categorized" className="rounded-none">Categorized</SelectItem>
                      <SelectItem value="phased" className="rounded-none">Phased</SelectItem>
                      <SelectItem value="flat" className="rounded-none">Flat List</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-settings-button"
              className="bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white rounded-none h-10 px-6 font-medium transition-colors duration-150"
            >
              {saved ? (
                <>
                  <Check className="w-4 h-4 mr-1.5" />
                  Saved
                </>
              ) : saving ? (
                "Saving..."
              ) : (
                <>
                  <Save className="w-4 h-4 mr-1.5" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
